import React from "react";

/**
 * React hook to provide handlers for keyboard and mouse events on dropdown
 * trigger button and dropdown menu. Can be used by dropdown menus to add
 * some accessible features like keyboard handling and option focus.
 */
export function useDropdownMenuHandlers({
  closeMenu,
  focusOnTrigger,
  openMenu,
  options,
  toggleMenu,
}: {
  /**
   * Function to close the dropdown menu
   */
  closeMenu: () => void;
  /**
   * Function to focus on the trigger element
   */
  focusOnTrigger: () => void;
  /**
   * Function to open the dropdown menu
   */
  openMenu: () => void;
  /**
   * List of options in the dropdown menu
   */
  options: unknown[];
  /**
   * Function to toggle the dropdown menu
   */
  toggleMenu: () => void;
}): {
  /**
   * Index of currently highlighted option
   */
  highlightedIndex: number;
  /**
   * Handler for clicks on trigger element
   */
  triggerClickHandler: () => void;
  /**
   * Handler for key presses on trigger element
   */
  triggerKeyDownHandler: (e: React.KeyboardEvent) => void;
  /**
   * Handler for key presses in menu element
   */
  menuKeyDownHandler: (e: React.KeyboardEvent) => void;
  /**
   * Handler for mouse enter event on menu element
   */
  menuMouseEnterHandler: () => void;
} {
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(0);

  const findNextIndex = findNextIndexInOptions(options);
  const findPrevIndex = findPrevIndexInOptions(options);

  const triggerClickHandler = () => {
    toggleMenu();
    // don't highlight any option on click
    setHighlightedIndex(-1);
  };

  const triggerKeyDownHandler = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      openMenu();
      setHighlightedIndex(findNextIndex());
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      openMenu();
      setHighlightedIndex(findPrevIndex());
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleMenu();
      setHighlightedIndex(findNextIndex());
    }
  };

  const menuKeyDownHandler = (e: React.KeyboardEvent) => {
    e.preventDefault();

    switch (e.key) {
      case "Escape": {
        closeMenu();
        focusOnTrigger();
        break;
      }
      case "Enter": {
        // istanbul ignore else - noop, not possible to test without gnarly hacks
        if (e.target) {
          // Click on the currently focused item. The target should be a menu
          // option and should handle the open state of the dropdown menu. If
          // if it's not a clickable element, the click event will be ignored.
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          (e.target as HTMLElement)?.click?.();
        }
        break;
      }
      case "Tab": {
        e.persist(); // Necessary for shiftKey
        const updatedHighlightedIndex = e.shiftKey
          ? findPrevIndex(highlightedIndex)
          : findNextIndex(highlightedIndex);
        setHighlightedIndex(updatedHighlightedIndex);
        break;
      }
      case "ArrowDown": {
        setHighlightedIndex(findNextIndex(highlightedIndex));
        break;
      }
      case "ArrowUp": {
        setHighlightedIndex(findPrevIndex(highlightedIndex));
        break;
      }
      case "Home": {
        setHighlightedIndex(findNextIndex());
        break;
      }
      case "End": {
        setHighlightedIndex(findPrevIndex());
        break;
      }
    }
  };

  const menuMouseEnterHandler = () => {
    setHighlightedIndex(-1);
    focusOnTrigger();
  };

  return {
    highlightedIndex,
    triggerClickHandler,
    triggerKeyDownHandler,
    menuKeyDownHandler,
    menuMouseEnterHandler,
  };
}

/**
 * This function finds the index of an available option going forward
 */
function findNextIndexInOptions(options: any[]) {
  return (currentIndex?: number): number => {
    if (options.length === 0) {
      return -1; // return -1 if there are no focusable options
    }
    if (typeof currentIndex === "undefined") {
      // If no currentIndex provided, assume the beginning of the list
      return 0;
    }

    const nextIndex = (currentIndex + 1) % options.length;

    return nextIndex;
  };
}

/**
 * This function finds the index of an available option going backwards
 */
function findPrevIndexInOptions(options: any[]) {
  return (currentIndex?: number): number => {
    if (options.length === 0) {
      return -1; // return -1 if there are no focusable options
    }
    if (typeof currentIndex === "undefined") {
      // If no currentIndex provided, assume the end of the list
      return options.length - 1;
    }

    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = options.length - 1;
    }

    return prevIndex;
  };
}
